import generateMethodKey from '../../lib/generateMethodKey';
import { updateStep2Complete } from '../page';
import {
  BOXLESS,
  DROP_OFF,
  METHODS_WITH_NPS_SCORES,
  PICKUP,
  PREFERRED,
} from '../../constants/returnMethods';
import { fetchPaymentIntent, receiveFee } from '../stripe';
import { fetchPickupData } from '../pickup';
import {
  SET_IS_CONSOLIDATED,
  receiveSelectionRequired,
  receiveConsolidatedLocations,
} from '../storeLocator';
import { getAllFees, getFeeTag } from '../../lib/consolidatedMethods';
import DEFAULT_COPY from '../../constants/defaultCopy';
import { fetchCarrierPickupData } from '../carrierPickup';
import { receiveUserPrefApplied } from '../userPreferences';
import { titan } from '../../lib/productAnalytics';
import { PRODUCT_LIST_IDS, TITAN_EVENTS } from '../../constants/titan';

export const RECEIVE_RETURN_OPTIONS = 'RECEIVE_RETURN_OPTIONS';
export const BYPASS_STEP = 'BYPASS_STEP';
export const RECEIVE_RETURN_OPTION_METHODS = 'RECEIVE_RETURN_OPTION_METHODS';
export const SELECT_RETURN_OPTION = 'SELECT_RETURN_OPTION';
export const SELECT_RETURN_OPTION_METHOD = 'SELECT_RETURN_OPTION_METHOD';
export const RECEIVE_AND_SELECT_RETURN_OPTION_METHOD = 'RECEIVE_AND_SELECT_RETURN_OPTION_METHOD';
export const RETURN_OPTION_VISIBILITY = 'RETURN_OPTION_VISIBILITY';
export const RECEIVE_CONSOLIDATED_OPTIONS = 'RECEIVE_CONSOLIDATED_OPTIONS';
export const ENABLE_AVAILABLE_OPTIONS_API_FLOW = 'ENABLE_AVAILABLE_OPTIONS_API_FLOW';
export const RECEIVE_RETURN_PROHIBITED = 'RECEIVE_RETURN_PROHIBITED';

export const selectMethod = (selectedOptionMethodKey) => async (dispatch, getState) => {
  const {
    stripe: { service: stripeService, narvarPaymentId, paymentIntentSecret, completedPayment } = {},
    returnOptions: {
      returnOptionMethods: {
        [selectedOptionMethodKey]: {
          id: selectedMethodId,
          carrier,
          disable_locations_requirement: disableLocationsRequirement,
          return_cost: { service_fee: serviceFee } = {},
          pickup: { enabled: pickupEnabled } = {},
          store_locator: locationsEnabled,
        } = {},
      } = {},
    } = {},
  } = getState();
  const requiresPayment = serviceFee > 0 && !completedPayment;

  if (
    METHODS_WITH_NPS_SCORES.includes(selectedMethodId) &&
    (!narvarPaymentId || !paymentIntentSecret) &&
    stripeService !== selectedOptionMethodKey
  ) {
    const paymentId = selectedMethodId === BOXLESS ? selectedOptionMethodKey : selectedMethodId;
    dispatch(fetchPaymentIntent(paymentId));
  }

  if (pickupEnabled) {
    await dispatch(fetchPickupData({ service: selectedOptionMethodKey, carrier }));
  }

  if (selectedMethodId === PICKUP) {
    dispatch(fetchCarrierPickupData(selectedOptionMethodKey));
  }

  if (requiresPayment) {
    await dispatch(updateStep2Complete(false));
    await dispatch(receiveFee(serviceFee));
  }

  if (locationsEnabled && !disableLocationsRequirement) {
    await dispatch(receiveSelectionRequired(true));
    await dispatch(updateStep2Complete(false));
  } else {
    await dispatch(updateStep2Complete(true));
  }

  await dispatch({
    type: SELECT_RETURN_OPTION_METHOD,
    data: {
      selectedOptionMethodKey,
    },
  });
};

export const selectOption = (selectedOptionId) => async (dispatch, getState) => {
  const {
    returnOptions: { returnOptions },
  } = getState();
  const selectedOption = returnOptions.find(({ id }) => id === selectedOptionId);

  dispatch({
    type: SET_IS_CONSOLIDATED,
    data: {
      isConsolidated: Boolean(selectedOption.locations),
    },
  });

  if ((selectedOption.methods && selectedOption.methods.length !== 1) || selectedOption.locations) {
    return dispatch({
      type: SELECT_RETURN_OPTION,
      data: {
        selectedOptionId,
      },
    });
  }

  const selectedMethod = selectedOption.methods[0];
  const selectedOptionMethodKey = generateMethodKey(selectedMethod);

  await dispatch({
    type: SELECT_RETURN_OPTION,
    data: {
      selectedOptionId,
    },
  });
  return dispatch(selectMethod(selectedOptionMethodKey));
};

export const bypassStep = (bypassSteps) => async (dispatch) =>
  dispatch({
    type: BYPASS_STEP,
    data: {
      bypassSteps,
    },
  });

export const selectDefaultOption = () => async (dispatch, getState) => {
  const {
    returnOptions: { returnOptionVisibility = [] },
    page: { giftOrder = false } = {},
  } = getState();

  if (!returnOptionVisibility) return;

  const preferredOption = returnOptionVisibility.find(
    (opt) =>
      opt.display_method_user_preference === PREFERRED ||
      (opt.methods &&
        opt.methods.find((method) => method.display_method_user_preference === PREFERRED)),
  );
  const onlyOneOption = returnOptionVisibility.length === 1;

  if (preferredOption && !giftOrder) {
    await dispatch(selectOption(preferredOption.id));
    dispatch(receiveUserPrefApplied(true));
  } else if (onlyOneOption) {
    await dispatch(selectOption(returnOptionVisibility[0].id));
  }
};

export const createReturnOptions = () => async (dispatch, getState) => {
  const {
    page: { productType },
    returnOptions: { returnOptionMethods },
    returnsSettings: { [productType]: { returnOptions: { options: settingsOptions } = {} } = {} },
  } = getState();

  const returnOptions = settingsOptions.reduce((finalOptions, option) => {
    const formattedMethods = option.methods
      ? option.methods.reduce((finalMethods, methodKey) => {
          const methodData = returnOptionMethods[methodKey];
          if (methodData) {
            finalMethods.push({ ...methodData });
          }
          return finalMethods;
        }, [])
      : [];

    return formattedMethods.length
      ? [
          ...finalOptions,
          {
            ...option,
            methods: formattedMethods,
          },
        ]
      : finalOptions;
  }, []);

  return dispatch({
    type: RECEIVE_RETURN_OPTIONS,
    data: {
      returnOptions,
    },
  });
};

export const receiveApiReturnOptions = (apiReturnOptions) => async (dispatch, getState) => {
  const {
    page: { productType } = {},
    returnsSettings: {
      [productType]: { returnOptions: { methods: settingsMethodMap } = {} } = {},
    } = {},
  } = getState();

  const returnOptionMethods = apiReturnOptions.reduce((finalMap, apiOption) => {
    const methodKey = generateMethodKey(apiOption);
    const settingsMethod = settingsMethodMap[methodKey];
    const mergedMethod = { ...settingsMethod, ...apiOption };
    finalMap[methodKey] = mergedMethod;
    return finalMap;
  }, {});

  await dispatch({
    type: RECEIVE_RETURN_OPTION_METHODS,
    data: {
      returnOptionMethods,
    },
  });

  return dispatch(createReturnOptions());
};

export const receiveConsolidatedMethods = (consolidatedMethods) => async (dispatch, getState) => {
  const {
    language,
    page: { productType, locale, languageCode } = {},
    orderInfo: { currentOrderNumber } = {},
    returnsSettings: {
      [productType]: {
        returnOptions: { options: settingsOptions, methods: settingsMethodMap } = {},
      } = {},
    } = {},
    storeLocator: { postalCode, locationTagSettings } = {},
  } = getState();
  const { [languageCode]: DEFAULT_LANG_COPY = {} } = DEFAULT_COPY;

  const returnOptionMethods = consolidatedMethods.reduce((acc, apiOption) => {
    const methodKey = generateMethodKey(apiOption);
    const settingsMethod = settingsMethodMap[methodKey];
    const mergedMethod = apiOption.display_method
      ? { ...settingsMethod, ...apiOption, id: apiOption.display_method }
      : { ...settingsMethod, ...apiOption };
    acc[methodKey] = mergedMethod;
    return acc;
  }, {});

  const orderedOptions = [
    ...(returnOptionMethods[DROP_OFF] ? [{ id: DROP_OFF }] : []),
    ...settingsOptions,
  ];

  const returnOptions = orderedOptions.reduce((acc, option) => {
    const locationBasedOption = consolidatedMethods.find(
      (opt) => opt.locations && opt.display_method === option.id,
    );
    const formattedMethods =
      option.methods &&
      option.methods.reduce((finalMethods, methodKey) => {
        if (locationBasedOption) {
          const carrierIndex = methodKey.lastIndexOf('_');
          const [method, carrier] =
            carrierIndex > -1
              ? [methodKey.slice(0, carrierIndex), methodKey.slice(carrierIndex + 1)]
              : [methodKey, undefined];
          finalMethods.push({
            ...settingsMethodMap[methodKey],
            carrier,
            return_method: method,
            return_cost: locationBasedOption.return_cost,
          });
        } else if (returnOptionMethods[methodKey]) {
          finalMethods.push({ ...returnOptionMethods[methodKey] });
        }
        return finalMethods;
      }, []);

    return locationBasedOption || (formattedMethods && formattedMethods.length)
      ? [
          ...acc,
          {
            ...option,
            ...(locationBasedOption || {}),
            methods: formattedMethods,
          },
        ]
      : acc;
  }, []);

  returnOptions.forEach((option) => {
    const { shippingFees } = getAllFees(option);
    if (option.locations) {
      dispatch(
        receiveConsolidatedLocations({
          postalCode,
          option,
          locations: option.locations.map((location) => {
            const {
              address,
              additional_details: additionalDetails,
              return_info: { preferred_method_carrier: { return_method: returnMethod } = {} } = {},
            } = location;
            const businessInfo = (additionalDetails && additionalDetails.business_info) || [];
            const methodInstructions =
              DEFAULT_LANG_COPY[`returns.return_options.locations.${returnMethod}.instructions`];
            return {
              ...location,
              fee:
                shippingFees.length > 1
                  ? getFeeTag({ option, location, language, locale })
                  : undefined,
              ...(option.id === DROP_OFF
                ? {
                    tag: {
                      style: locationTagSettings.methods.style,
                      value: locationTagSettings.methods.content[returnMethod],
                    },
                    additional_details: address
                      ? {
                          ...(additionalDetails || {}),
                          business_info: [
                            ...businessInfo,
                            ...(methodInstructions
                              ? [
                                  {
                                    icon: 'info',
                                    value: methodInstructions,
                                  },
                                ]
                              : []),
                          ],
                        }
                      : undefined,
                  }
                : {}),
            };
          }),
        }),
      );
    }
  });

  titan.event(TITAN_EVENTS.PRODUCT_LIST_VIEWED, {
    order_id: currentOrderNumber,
    list_id: PRODUCT_LIST_IDS.PUDO_OPTIONS,
    products: returnOptions.map((option) => ({
      product_id: option.id,
      variant: option.display_method_user_preference,
    })),
  });

  return dispatch({
    type: RECEIVE_CONSOLIDATED_OPTIONS,
    data: {
      returnOptions,
      returnOptionMethods,
    },
  });
};

export const receiveReturnProhibited = () => ({
  type: RECEIVE_RETURN_PROHIBITED,
});
